import React from 'react';

import { Button, Stack } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { normalizeEmail } from '@clearverify/utils';

import { EmployeeSearchInputKindEnum } from 'src/api';
import { TextField } from 'src/components/finalForm';
import type { EmployeeSearchCriteria } from 'src/types/employeeSearch';

import { EmailSearchFormFields } from './constants';
import type { EmailSearchFormValues } from './types';
import { emailSearchFormValidationSchema } from './validation';

type EmailSearchFormProps = {
  onSubmit: (criteria: EmployeeSearchCriteria) => void;
  loading?: boolean;
  /** Search restored from the URL; prefills the form when it's an email search. */
  initialCriteria?: EmployeeSearchCriteria | null;
};

export const EmailSearchForm: React.FC<EmailSearchFormProps> = ({
  onSubmit,
  loading,
  initialCriteria,
}) => {
  const handleSubmit = React.useCallback(
    (values: EmailSearchFormValues) =>
      onSubmit({
        kind: EmployeeSearchInputKindEnum.Email,
        email: values.email ? normalizeEmail(values.email) : undefined,
      }),
    [onSubmit],
  );

  // Held in state so the reference stays stable — `Form` reinitializes on a new one.
  const [initialValues] = React.useState<Partial<EmailSearchFormValues>>(() =>
    initialCriteria?.kind === EmployeeSearchInputKindEnum.Email
      ? { [EmailSearchFormFields.email]: initialCriteria.email }
      : EMAIL_INITIAL_VALUES,
  );

  return (
    <Form<EmailSearchFormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={emailSearchFormValidationSchema}
      subscription={{ submitting: true, valid: true, dirty: true }}
      containerProps={{ mt: 0 }}
    >
      {({ submitting, valid, dirty }) => (
        <React.Fragment>
          <Field
            name={EmailSearchFormFields.email}
            component={TextField}
            label="Registered user email"
            placeholder="user@example.com"
            type="email"
            disabled={submitting || loading}
            fullWidth
          />

          <Stack direction="row" justifyContent="flex-end" mt={3}>
            <Button
              type="submit"
              variant="contained"
              disabled={!valid || submitting || loading || !dirty}
              loading={submitting || loading}
            >
              Run search
            </Button>
          </Stack>
        </React.Fragment>
      )}
    </Form>
  );
};

const EMAIL_INITIAL_VALUES: Partial<EmailSearchFormValues> = {
  [EmailSearchFormFields.email]: undefined,
};
